import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import moment from 'moment';

import useTopPanel from 'hooks/useTopPanel';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import DoughnutChart from 'components/ui/charts/DoughnutChart';

const LEAD_COLORS = { issiq: '#EA2027', iliq: '#F59E0B', sovuq: '#0652DD' };
const LEAD_LABELS = { issiq: 'Issiq', iliq: 'Iliq', sovuq: 'Sovuq' };

const num = (v, suffix = '') =>
  v === null || v === undefined ? '—' : `${v}${suffix}`;

const AiAnalytics = () => {
  const { t, i18n } = useTranslation();
  const { setComponent } = useTopPanel();
  const axiosPrivate = useAxiosPrivate();
  const [selectedOp, setSelectedOp] = useState(null);

  useEffect(() => {
    setComponent(
      <div className='component-title'>AI tahlillari (SSAi)</div>
    );
  }, [i18n.language]);

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['/ssai/stats'],
    queryFn: async () => (await axiosPrivate.get('/ssai/stats')).data.data,
    retry: false,
  });

  const { data: operators, isLoading: opsLoading } = useQuery({
    queryKey: ['/ssai/operators'],
    queryFn: async () => (await axiosPrivate.get('/ssai/operators')).data.data,
    retry: false,
  });

  const leads = stats?.leads || {};
  const leadKeys = ['issiq', 'iliq', 'sovuq'];
  const leadTotal = leadKeys.reduce((s, k) => s + (leads[k] || 0), 0);

  return (
    <div className='flex flex-col gap-4'>
      {/* Statistika kartalari */}
      <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3'>
        <StatCard
          title='Jami qo‘ng‘iroq'
          value={num(stats?.total_calls)}
          icon='bi-telephone'
          color='#0652DD'
          loading={statsLoading}
        />
        <StatCard
          title='Baholandi'
          value={num(stats?.done)}
          icon='bi-check2-circle'
          color='#079992'
          loading={statsLoading}
        />
        <StatCard
          title='Jarayonda'
          value={num(stats?.processing)}
          icon='bi-hourglass-split'
          color='#F59E0B'
          loading={statsLoading}
        />
        <StatCard
          title='Xatolik'
          value={num(stats?.failed)}
          icon='bi-x-octagon'
          color='#EA2027'
          loading={statsLoading}
        />
        <StatCard
          title='O‘rtacha ball'
          value={num(
            stats?.avg_score != null ? Math.round(stats.avg_score) : null
          )}
          icon='bi-star'
          color='#7C5CFA'
          loading={statsLoading}
        />
        <StatCard
          title='Salomlashish'
          value={num(
            stats?.greeting_rate != null
              ? Math.round(stats.greeting_rate)
              : null,
            '%'
          )}
          icon='bi-emoji-smile'
          color='#2894EC'
          loading={statsLoading}
        />
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4'>
        {/* Lid taqsimoti */}
        <div className='!base-border !shadow-md p-4 rounded-lg'>
          <div className='font-semibold mb-3'>Lid taqsimoti</div>
          {leadTotal > 0 ? (
            <div className='relative h-72'>
              <DoughnutChart
                chartLabels={leadKeys.map((k) => LEAD_LABELS[k])}
                chartDatasets={[
                  {
                    data: leadKeys.map((k) => leads[k] || 0),
                    backgroundColor: leadKeys.map((k) => LEAD_COLORS[k]),
                    borderWidth: 2,
                    hoverOffset: 12,
                    hoverBorderWidth: 0,
                    borderRadius: 4,
                  },
                ]}
                suffix=''
                optionPlugins={{ legend: { display: false } }}
              />
              <div className='text-center absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'>
                <h1 className='text-[30px]'>{leadTotal}</h1>
                <p className='text-xs text-gray-500'>{t('common.table.total')}</p>
              </div>
            </div>
          ) : (
            <Empty loading={statsLoading} />
          )}
          <div className='flex justify-center gap-4 mt-3 text-sm'>
            {leadKeys.map((k) => (
              <span key={k} className='flex items-center gap-1'>
                <span
                  className='inline-block w-3 h-3 rounded-full'
                  style={{ backgroundColor: LEAD_COLORS[k] }}
                />
                {LEAD_LABELS[k]}: <b>{leads[k] || 0}</b>
              </span>
            ))}
          </div>
        </div>

        {/* Operatorlar jadvali */}
        <div className='lg:col-span-2 !base-border !shadow-md p-4 rounded-lg overflow-x-auto'>
          <div className='font-semibold mb-3'>Operatorlar kesimida</div>
          {opsLoading ? (
            <Empty loading />
          ) : operators?.length ? (
            <table className='w-full text-sm'>
              <thead>
                <tr className='text-left text-gray-500 border-b'>
                  <th className='py-2'>Operator</th>
                  <th className='py-2 text-center'>Qo‘ng‘iroq</th>
                  <th className='py-2 text-center'>Baholandi</th>
                  <th className='py-2 text-center'>O‘rt. ball</th>
                  <th className='py-2 text-center'>Salom.</th>
                  <th className='py-2 text-center'>🔥/🌤/❄️</th>
                </tr>
              </thead>
              <tbody>
                {operators.map((o, i) => (
                  <tr
                    key={i}
                    className='border-b hover:bg-purple-50 cursor-pointer'
                    onClick={() => setSelectedOp(o?.operator)}
                    title='Qo‘ng‘iroqlarni ko‘rish'
                  >
                    <td className='py-2 text-blue-600'>
                      {o?.operator?.name || '—'}
                    </td>
                    <td className='py-2 text-center'>{num(o?.total_calls)}</td>
                    <td className='py-2 text-center'>{num(o?.evaluated)}</td>
                    <td className='py-2 text-center font-semibold'>
                      {num(o?.avg_score != null ? Math.round(o.avg_score) : null)}
                    </td>
                    <td className='py-2 text-center'>
                      {num(
                        o?.greeting_rate != null
                          ? Math.round(o.greeting_rate)
                          : null,
                        '%'
                      )}
                    </td>
                    <td className='py-2 text-center whitespace-nowrap'>
                      <span className='text-red-600'>{o?.leads?.issiq || 0}</span>
                      {' / '}
                      <span className='text-amber-500'>{o?.leads?.iliq || 0}</span>
                      {' / '}
                      <span className='text-blue-600'>{o?.leads?.sovuq || 0}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty loading={false} />
          )}
        </div>
      </div>

      <OperatorCallsModal
        operator={selectedOp}
        onClose={() => setSelectedOp(null)}
      />
    </div>
  );
};

// ── Operator qo'ng'iroqlari modali ──
const QualityChip = ({ status }) => {
  const map = {
    issiq: { label: '🔥 Issiq', cls: 'bg-red-50 text-red-600' },
    iliq: { label: '🌤 Iliq', cls: 'bg-amber-50 text-amber-600' },
    sovuq: { label: '❄️ Sovuq', cls: 'bg-blue-50 text-blue-600' },
  };
  const q = map[status];
  if (!q) return <span className='text-gray-300'>—</span>;
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${q.cls}`}>
      {q.label}
    </span>
  );
};

const CallRow = ({ call }) => {
  const axiosPrivate = useAxiosPrivate();
  const [open, setOpen] = useState(false);

  const { data: detail, isLoading } = useQuery({
    queryKey: ['/ssai/calls', call?.uuid],
    queryFn: async () =>
      (await axiosPrivate.get(`/ssai/calls/${call?.uuid}`)).data.data,
    enabled: open && !!call?.uuid,
    retry: false,
    staleTime: 1000 * 60,
  });

  return (
    <>
      <tr className='border-b hover:bg-gray-50'>
        <td className='py-2'>
          {call?.created_at
            ? moment(call.created_at).format('DD.MM.YYYY HH:mm')
            : '—'}
        </td>
        <td className='py-2 text-center'>
          {call?.duration != null ? `${Math.round(call.duration)}s` : '—'}
        </td>
        <td className='py-2 text-center font-semibold'>
          {call?.score != null ? call.score : '—'}
        </td>
        <td className='py-2 text-center'>
          <QualityChip status={call?.lead_status} />
        </td>
        <td className='py-2 text-center'>
          {call?.greeted == null ? '—' : call.greeted ? '✓' : '✗'}
        </td>
        <td className='py-2 text-center'>
          <button
            type='button'
            className='text-blue-600 text-xs hover:underline'
            onClick={() => setOpen((o) => !o)}
          >
            {open ? 'Yashirish' : 'Batafsil'}
          </button>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={6} className='bg-[#FAFAFE] px-3 py-2'>
            {isLoading ? (
              <div className='py-3 text-center'>
                <CircularProgress size={20} />
              </div>
            ) : detail ? (
              <div className='text-xs text-gray-700'>
                {detail?.evaluation?.summary && (
                  <p className='mb-1'>
                    <b>Xulosa:</b> {detail.evaluation.summary}
                  </p>
                )}
                {detail?.evaluation?.lead_reason && (
                  <p className='mb-1 text-gray-500'>
                    <i className='bi bi-info-circle mr-1' />
                    {detail.evaluation.lead_reason}
                  </p>
                )}
                {detail?.full_text && (
                  <div className='mt-1 bg-white rounded-md p-2 max-h-52 overflow-y-auto whitespace-pre-line border border-[#EEE]'>
                    {detail.full_text}
                  </div>
                )}
              </div>
            ) : (
              <span className='text-gray-400 text-xs'>Natija yo'q</span>
            )}
          </td>
        </tr>
      )}
    </>
  );
};

const OperatorCallsModal = ({ operator, onClose }) => {
  const axiosPrivate = useAxiosPrivate();
  const num = operator?.external_id;

  const { data, isLoading } = useQuery({
    queryKey: ['/ssai/operators/calls', num],
    queryFn: async () =>
      (await axiosPrivate.get(`/ssai/operators/${num}/calls`)).data.data,
    enabled: !!num,
    retry: false,
  });

  const calls = data?.data || [];

  return (
    <Dialog
      open={!!operator}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{ className: '!base-border' }}
    >
      <DialogTitle className='!bg-[#CDCDCDC7] flex items-center justify-between'>
        <span className='font-semibold text-[18px]'>
          {operator?.name} — qo‘ng‘iroqlar tahlili
        </span>
        <IconButton size='small' onClick={onClose}>
          <i className='bi bi-x-lg text-base' />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <div className='h-40 flex items-center justify-center'>
            <CircularProgress size={28} />
          </div>
        ) : calls.length ? (
          <table className='w-full text-sm'>
            <thead>
              <tr className='text-left text-gray-500 border-b'>
                <th className='py-2'>Sana</th>
                <th className='py-2 text-center'>Davomiyligi</th>
                <th className='py-2 text-center'>Ball</th>
                <th className='py-2 text-center'>Lid sifati</th>
                <th className='py-2 text-center'>Salom.</th>
                <th className='py-2 text-center'></th>
              </tr>
            </thead>
            <tbody>
              {calls.map((c, i) => (
                <CallRow key={c?.uuid || i} call={c} />
              ))}
            </tbody>
          </table>
        ) : (
          <div className='h-40 flex items-center justify-center text-gray-400'>
            <i className='bi bi-exclamation-octagon mr-1' /> Qo'ng'iroq yo'q
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const StatCard = ({ title, value, icon, color, loading }) => (
  <div className='!base-border !shadow-sm p-3 rounded-lg flex items-center gap-3 bg-white'>
    <div
      className='w-10 h-10 rounded-lg flex items-center justify-center shrink-0'
      style={{ backgroundColor: `${color}1A`, color }}
    >
      <i className={`bi ${icon} text-lg`} />
    </div>
    <div className='min-w-0'>
      <div className='text-[22px] font-bold leading-none'>
        {loading ? '…' : value}
      </div>
      <div className='text-xs text-gray-500 mt-0.5'>{title}</div>
    </div>
  </div>
);

const Empty = ({ loading }) => (
  <div className='h-60 flex items-center justify-center text-gray-400'>
    {loading ? (
      <CircularProgress size={28} color='inherit' />
    ) : (
      <span>
        <i className='bi bi-exclamation-octagon mr-1' /> Ma'lumot yo'q
      </span>
    )}
  </div>
);

export default memo(AiAnalytics);
